import { useState, useMemo, useEffect } from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { InstallSectionCard } from "../InstallSectionCard";
import { FaiByDepartmentTable } from "./FaiByDepartmentTable";
import { useTaxonomyContext } from "../../../../../../contexts/Taxonomy/TaxonomyContext";
import { HgFaiDepartment, HgFaiRole } from "../../../../../../types/taxonomy";
import {
  FAI_PRODUCTS_BY_DEPARTMENT_AND_ROLE_QUERY,
  FAIProductsByDepartmentAndRoleQueryPayload,
} from "../../../../../../legacy/queries/FAI";
import { useCompanyPageStore } from "../../../../../../store/companyPageStore";
import { COMPANY_SEARCH_CLIENT } from "../../../../../../utils/consts";
import { useQuery } from "@apollo/client";
import { useLocalStorage } from "../../../../../../utils/useLocalStorage";
import { LightTooltip } from "../../../../../../components/LightTooltip";

const FAI_SELECTED_DEPARTMENT_LOCAL_STORAGE_KEY = "FAI_SELECTED_DEPARTMENT";
const FAI_SELECTED_ROLE_LOCAL_STORAGE_KEY = "FAI_SELECTED_ROLE";
const IT_DEPARTMENT_ID = "D32106BCB6DE321930CF34574EA388C";

export const FaiByDepartment = () => {
  const company = useCompanyPageStore((state) => state.company);
  const { faiDepartments } = useTaxonomyContext();

  const [selectedDepartment, setSelectedDepartment] = useLocalStorage<
    HgFaiDepartment | undefined
  >(FAI_SELECTED_DEPARTMENT_LOCAL_STORAGE_KEY, undefined);

  const [selectedRole, setSelectedRole] = useLocalStorage<HgFaiRole>(
    FAI_SELECTED_ROLE_LOCAL_STORAGE_KEY,
    { id: "any", name: "any" }
  );

  const [department, setDepartment] = useState<HgFaiDepartment["id"]>("");
  const [role, setRole] = useState<HgFaiRole["id"]>("any");

  // Initialize state from localStorage
  useEffect(() => {
    if (faiDepartments && faiDepartments.length > 0) {
      let deptToUse = selectedDepartment;

      // Validate that the selected department exists in the current faiDepartments
      if (
        deptToUse &&
        !faiDepartments.find((dept) => dept.id === deptToUse?.id)
      ) {
        deptToUse = undefined;
      }

      // If no valid department in localStorage, use IT department as default
      if (!deptToUse) {
        setSelectedDepartment(
          faiDepartments.find((dept) => dept.id === IT_DEPARTMENT_ID) ||
            faiDepartments[0]
        );
      }

      if (deptToUse) {
        setDepartment(deptToUse.id);

        // Validate that the selected role exists in the current department
        const isValidRole =
          selectedRole.id === "any" ||
          deptToUse.roles?.some((role) => role.id === selectedRole.id);

        if (isValidRole) {
          setRole(selectedRole.id);
        } else {
          // Reset to "any" if the role doesn't exist in this department
          setRole("any");
          setSelectedRole({ id: "any", name: "any" });
        }
      } else {
        // No valid department found, reset everything
        setDepartment("");
        setRole("any");
      }
    }
  }, [
    faiDepartments,
    selectedDepartment,
    selectedRole,
    setSelectedDepartment,
    setSelectedRole,
  ]);

  const currentDepartment = useMemo(
    () => faiDepartments?.find((dept) => dept.id === department) || null,
    [faiDepartments, department]
  );

  const currentRole = useMemo(
    () =>
      currentDepartment?.roles?.find((depRole) => depRole.id === role) || null,
    [currentDepartment?.roles, role]
  );

  const roleSelectLabel = useMemo(
    () => (currentDepartment ? `${currentDepartment.name} Role` : "Role"),
    [currentDepartment]
  );

  const hasRoles = useMemo(
    () => currentDepartment?.roles && currentDepartment.roles.length > 0,
    [currentDepartment]
  );

  // Handle department change
  const handleDepartmentChange = (departmentId: string) => {
    setDepartment(departmentId);
    setRole("any");

    const newDepartment = faiDepartments?.find(
      (dept) => dept.id === departmentId
    );
    if (newDepartment) {
      setSelectedDepartment(newDepartment);
      setSelectedRole({ id: "any", name: "any" });
    }
  };

  // Handle role change
  const handleRoleChange = (roleId: string) => {
    setRole(roleId);

    if (roleId === "any") {
      setSelectedRole({ id: "any", name: "any" });
    } else {
      const newRole = currentDepartment?.roles?.find((r) => r.id === roleId);
      if (newRole) {
        setSelectedRole(newRole);
      }
    }
  };

  const installFilters = useCompanyPageStore((state) => state.installFilters);

  const { data, loading } =
    useQuery<FAIProductsByDepartmentAndRoleQueryPayload>(
      FAI_PRODUCTS_BY_DEPARTMENT_AND_ROLE_QUERY,
      {
        variables: {
          hgId: company!.hgId,
          page: 0,
          per: 10,
          countryAlpha2s: installFilters.countryAlpha2s,
          productIds: installFilters.productIds,
          vendorIds: installFilters.vendorIds,
          productCategoryIds: installFilters.productCategoryIds,
          productAttributeIds: installFilters.productAttributeIds,
          departmentId: currentDepartment?.id,
          roleId: currentRole?.id,
        },
        context: {
          clientName: COMPANY_SEARCH_CLIENT,
        },
        skip: !currentDepartment?.id,
      }
    );

  return (
    <InstallSectionCard
      title="Functional Area Intelligence (FAI) by Department"
      height={720}
      noTitleMargin
      filterKey="FAI_BY_DEPARTMENT"
    >
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ my: 2, height: 60 }}
      >
        Select a department below to receive location-based intelligence powered
        by FAI. Some departments may be further refined by roles associated with
        that department. Only products matching the filter criteria at the top
        of this page will be shown.
      </Typography>

      <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
        <FormControl sx={{ width: 200 }}>
          <InputLabel variant="standard">Department</InputLabel>
          <Select
            data-testid="company-detail-install-fai-department-select"
            value={
              faiDepartments?.find((dept) => dept.id === department)
                ? department
                : ""
            }
            label="Department"
            variant="standard"
            onChange={(e) => handleDepartmentChange(e.target.value)}
          >
            {faiDepartments?.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ width: 200 }}>
          <InputLabel variant="standard">{roleSelectLabel}</InputLabel>
          <Select
            data-testid="company-detail-install-fai-role-select"
            value={
              role === "any" ||
              currentDepartment?.roles?.find((r) => r.id === role)
                ? role
                : "any"
            }
            label={roleSelectLabel}
            variant="standard"
            disabled={!hasRoles}
            onChange={(e) => handleRoleChange(e.target.value)}
          >
            <MenuItem value="any">Any Role</MenuItem>
            {currentDepartment?.roles?.map((roleItem) => (
              <MenuItem key={roleItem.id} value={roleItem.id}>
                {roleItem.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <LightTooltip
          title={
            <Typography variant="body2">
              Refine your selected department by any of its associated roles.
              Please note that not all departments will have defined roles.
            </Typography>
          }
        >
          <IconButton size="small" sx={{ alignSelf: "flex-end" }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </LightTooltip>
      </Stack>

      <Box data-testid="company-detail-install-fai-dept-table">
        <FaiByDepartmentTable
          data={data?.functionalAreaIntelligenceByCompany?.data}
          loading={loading}
          departmentName={currentDepartment?.name}
          roleName={currentRole?.name || (role === "any" ? "any" : undefined)}
        />
      </Box>
    </InstallSectionCard>
  );
};
